import { useCallback } from 'react';
import {
  ReactFlow,
  type Node,
  type Edge,
  type OnConnect,
  type OnNodesChange,
  type OnEdgesChange,
} from '@xyflow/react';
import { Workflow as WorkflowIcon } from 'lucide-react';
import { workflowNodeTypes } from './nodes';

import '@xyflow/react/dist/style.css';

export interface WorkflowCanvasProps {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;
  onDrop: (e: React.DragEvent) => void;
  onSelectionChange?: (node: Node | null) => void;
  className?: string;
}

export function WorkflowCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onDrop,
  onSelectionChange,
  className,
}: WorkflowCanvasProps) {
  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      onSelectionChange?.(node);
    },
    [onSelectionChange]
  );

  const onPaneClick = useCallback(() => {
    onSelectionChange?.(null);
  }, [onSelectionChange]);

  const isEmpty = nodes.length === 0;

  return (
    <div className={className ?? 'h-full w-full'} onDragOver={onDragOver} onDrop={onDrop}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={workflowNodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.25}
        maxZoom={1.5}
        defaultEdgeOptions={{ type: 'smoothstep' }}
        snapToGrid
        snapGrid={[16, 16]}
        className="bg-muted/20"
        proOptions={{ hideAttribution: true }}
      >
        {isEmpty && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 text-center">
            <div className="rounded-full bg-muted p-4">
              <WorkflowIcon className="h-10 w-10 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">Start building your workflow</p>
            <p className="max-w-xs text-xs text-muted-foreground">
              Drag blocks from the sidebar or click a template to begin
            </p>
          </div>
        )}
      </ReactFlow>
    </div>
  );
}
